import cn from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import { CymbioSpinner } from '../../common/Spinner';
import { miraklListReturnCodes, miraklPostReturnCode } from '../../../store/actions/channels';
import Select from '../../common/Select';

import styles from './MiraklReturnCodeSelect.module.scss';

export function MiraklReturnCodeSelect(props) {
  const { supplierId, retailerId } = props;
  const dispatch = useDispatch();
  const {
    data: {
      mirakl: { returnCodes },
    },
    loading: { listReturnCodes: loadingListReturnCodes, postReturnCode: loadingPostReturnCode },
  } = useSelector((state) => state.channels);

  const options = useMemo(() => {
    if (isEmpty(returnCodes)) {
      return [];
    }

    return Object.keys(returnCodes).map((code) => ({
      label: returnCodes[code],
      value: code,
    }));
  }, [returnCodes]);

  const [selected, setSelected] = useState(options[0]?.value);

  useEffect(
    function listReturnCodes() {
      dispatch(miraklListReturnCodes({ supplierId, retailerId }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supplierId, retailerId],
  );

  useEffect(
    function initSelectedReturnCode() {
      if (!isEmpty(returnCodes) && !selected) {
        setSelected(Object.keys(returnCodes)[0]);
      }
    },
    [returnCodes, selected],
  );

  const onContinue = () => {
    dispatch(miraklPostReturnCode({ supplierId, retailerId, returnCode: selected }));
  };

  const onRefresh = () => {
    dispatch(miraklListReturnCodes({ supplierId, retailerId }));
  };

  if (loadingListReturnCodes) {
    return (
      <div className={styles.wrapper}>
        <h3>Getting return codes from Mirakl...</h3>
        <CymbioSpinner />
      </div>
    );
  }

  if (isEmpty(returnCodes)) {
    return (
      <div className={cn(styles.wrapper, styles.noReturnCodes)}>
        <h3>
          Our system has not detected any <strong>active return codes</strong> on your Mirakl shop,
          <br />
          please active a return code and try again.
        </h3>

        <div className={styles.actions}>
          <button onClick={onRefresh}>Refresh</button>
          <button disabled>Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <h3>Please choose which return code you want to use:</h3>
      <div className={styles.selectWrapper}>
        <Select options={options} onChange={setSelected} value={selected} />
        <button onClick={onContinue} disabled={loadingPostReturnCode || !selected}>
          Continue
        </button>
      </div>
    </div>
  );
}

MiraklReturnCodeSelect.propTypes = {
  supplierId: PropTypes.number.isRequired,
  retailerId: PropTypes.number.isRequired,
};
